import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { hasWebGL, isFullscreen, isTypingTarget, onFullscreenChange, toggleFullscreen } from "../bingo-shared/display";
import { fireHallBingoConfetti, HallBoard } from "./HallBoard";
import { HallAtmosphere } from "./HallAtmosphere";
import { connectHallDisplay } from "./sync";
import { useHallStore } from "./store";
import { setHallRole } from "./role";

export default function StagePage() {
  setHallRole("display");
  const rootRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [webgl, setWebgl] = useState(true);
  const [sceneFailed, setSceneFailed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hydrate = useHallStore((s) => s.hydrate);
  const phase = useHallStore((s) => s.phase);
  const called = useHallStore((s) => s.called);
  const current = useHallStore((s) => s.current);
  const theme = useHallStore((s) => s.theme);
  const mixSeconds = useHallStore((s) => s.mixSeconds);

  useEffect(() => {
    setMounted(true);
    setWebgl(hasWebGL());
    hydrate();
    return connectHallDisplay(() => {
      window.setTimeout(() => {
        if (!window.closed) setDismissed(true);
      }, 80);
    });
  }, [hydrate]);

  useEffect(() => {
    const sync = () => setFullscreen(isFullscreen());
    return onFullscreenChange(sync);
  }, []);

  useEffect(() => {
    if (phase !== "celebrating") return;
    fireHallBingoConfetti(theme);
  }, [phase, theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if ((e.key === "f" || e.key === "F") && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (rootRef.current) void toggleFullscreen(rootRef.current);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const sceneOn = mounted && webgl && !sceneFailed;

  if (dismissed) {
    return (
      <div className="bingo-v2 flex h-dvh flex-col items-center justify-center gap-4 px-6 text-center text-cream">
        <p className="font-display text-3xl font-semibold">Player view closed</p>
        <p className="max-w-sm text-sm text-cream-muted">The caller docked this window. You can close the tab.</p>
        <button
          type="button"
          onClick={() => {
            try {
              window.close();
            } catch {
              /* ignore */
            }
          }}
          className="rounded-2xl border border-hall-line bg-hall-elevated px-5 py-3 text-sm font-medium"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="bingo-v2 relative flex h-dvh flex-col overflow-hidden text-cream"
      data-phase={phase}
      data-called={called.length}
      data-theme={theme}
      data-mix={mixSeconds}
      data-stage="1"
    >
      <HallAtmosphere theme={theme} burst={phase === "celebrating"} />
      <header className="relative z-30 flex shrink-0 items-center gap-2 px-3 pt-[max(0.35rem,env(safe-area-inset-top))] pb-1 md:px-6 md:pt-[max(0.75rem,env(safe-area-inset-top))] md:pb-2">
        <div className="min-w-0 flex-1">
          <p className="hidden font-sans text-[0.65rem] font-medium tracking-[0.28em] text-cream-muted uppercase md:block">Player view</p>
          <h1 className="v2-title font-display text-[1.15rem] leading-none font-semibold tracking-tight md:text-3xl">Bingo Blast</h1>
        </div>
        <div className="flex items-center rounded-full border border-hall-line bg-hall-elevated px-2 py-1 font-sans text-xs tabular-nums text-cream-muted md:px-3 md:py-1.5 md:text-sm">
          {called.length}<span className="text-cream-muted/60">/75</span>
        </div>
        <button
          type="button"
          onClick={() => { if (rootRef.current) void toggleFullscreen(rootRef.current); }}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-hall-line bg-hall-elevated"
          aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {fullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
        </button>
      </header>
      <div className="relative z-[2] min-h-0 flex-1 basis-0 overflow-hidden px-1.5 pb-[max(0.45rem,env(safe-area-inset-bottom))] md:px-4 md:pb-4">
        <HallBoard sceneOn={sceneOn} onSceneFail={() => setSceneFailed(true)} />
      </div>
      <div className="sr-only" aria-live="assertive">
        {phase === "calling" && current != null ? `${current}` : ""}
      </div>
    </div>
  );
}
